import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './projects.css';
import { projects, ProjectType } from '../../projectsData';

const categories = ['Machine Learning', 'Software'];

// ── 3D tilt helpers ────────────────────────────────────────────────────────────
const handleCardTiltMove = (e: React.MouseEvent<HTMLDivElement>) => {
  const el = e.currentTarget;
  const rect = el.getBoundingClientRect();
  const x = (e.clientX - rect.left) / rect.width - 0.5;
  const y = (e.clientY - rect.top)  / rect.height - 0.5;
  el.style.transform  = `perspective(800px) rotateY(${x * 14}deg) rotateX(${-y * 14}deg) translateY(-10px) scale(1.02)`;
  el.style.boxShadow  = `${-x * 12}px ${-y * 12}px 30px rgba(var(--primary-color-rgb), 0.18)`;
};

const handleCardTiltLeave = (e: React.MouseEvent<HTMLDivElement>) => {
  const el = e.currentTarget;
  el.style.transform  = '';
  el.style.boxShadow  = '';
};

// ── Particle burst on card click ───────────────────────────────────────────────
const triggerBurst = (cx: number, cy: number) => {
  const canvas = document.createElement('canvas');
  canvas.width  = window.innerWidth;
  canvas.height = window.innerHeight;
  canvas.style.cssText =
    'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:1999;';
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d')!;

  // Read primary color from CSS (black in light, white in dark)
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  const r = isDark ? 255 : 0;
  const g = isDark ? 255 : 0;
  const b = isDark ? 255 : 0;

  const COUNT = 38;
  const particles = Array.from({ length: COUNT }, (_, i) => {
    const angle = (Math.PI * 2 * i) / COUNT + (Math.random() - 0.5) * 0.4;
    const speed = 2.5 + Math.random() * 5.5;
    return {
      x: cx, y: cy,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: 1,
      size: 2 + Math.random() * 2.5,
    };
  });

  let frame = 0;
  const animate = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    let alive = false;
    for (const p of particles) {
      p.x  += p.vx;
      p.y  += p.vy;
      p.vx *= 0.93;
      p.vy *= 0.93;
      p.life -= 0.028;
      if (p.life > 0) {
        alive = true;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * p.life, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r},${g},${b},${p.life * 0.85})`;
        ctx.fill();
      }
    }
    if (alive && ++frame < 70) {
      requestAnimationFrame(animate);
    } else {
      document.body.removeChild(canvas);
    }
  };
  requestAnimationFrame(animate);
};

const STAGGER_DELAY = 180; // ms between each card entrance

const Projects: React.FC = () => {
  const gridRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const enteredRef = useRef<Set<Element>>(new Set());
  const navigate = useNavigate();

  // IntersectionObserver — stagger cards when grid scrolls into view
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    gridRefs.current.forEach((gridEl) => {
      const obs = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          const cards = gridEl.querySelectorAll('.project__card');
          cards.forEach((card, i) => {
            if (enteredRef.current.has(card)) return;
            enteredRef.current.add(card);
            const enterAt = i * STAGGER_DELAY;
            setTimeout(() => {
              card.classList.add('project__card--entered');
              // After entrance transition (700ms), switch to snappy tilt transition
              setTimeout(() => card.classList.add('project__card--tilt-ready'), 750);
            }, enterAt);
          });
          obs.disconnect();
        }
      }, { threshold: 0.12 });
      obs.observe(gridEl);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, []);

  const handleProjectClick = (project: ProjectType, e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    triggerBurst(rect.left + rect.width / 2, rect.top + rect.height / 2);
    navigate(`/projects/${project.slug}`);
  };

  const isGoogleDeepMindProject = (title: string) => title.includes('Google DeepMind');

  return (
    <section className="projects section" id="projects">
      <div className="section__header">
        <h2 className="section__title">Projects</h2>
        <span className="section__subtitle">My recent work</span>
      </div>
      <div className="projects__categories">
        {categories.map((cat) => (
          <div key={cat} className="projects__category">
            <h3>{cat}</h3>
            <div
              className="projects__grid"
              ref={(el) => {
                if (el) gridRefs.current.set(cat, el);
                else gridRefs.current.delete(cat);
              }}
            >
              {projects.filter(p => p.category === cat).map(project => (
                <div
                  className={`project__card ${isGoogleDeepMindProject(project.title) ? 'project__card--deepmind' : ''}`}
                  key={project.title}
                  onClick={(e) => handleProjectClick(project, e)}
                  onMouseMove={handleCardTiltMove}
                  onMouseLeave={handleCardTiltLeave}
                >
                  <div className="project__card-beam" aria-hidden="true" />
                  <div className="project__clickable-hint">Click to view</div>
                  <div className="project__img-container">
                    <img src={project.image} alt={project.title} className="project__img" />
                  </div>
                  <h4>{project.title}</h4>
                  <p>{project.description}</p>
                  <div className="project__card-overlay">
                    <div className="project__view-btn">View Details</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Projects;
